if (!customElements.get('andersen-hero-slider')) {
  customElements.define('andersen-hero-slider', class AndersenHeroSlider extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.timer = null;
      this.userPaused = false;
      this.hoverPaused = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onResize = this.onResize.bind(this);
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchStartTime = 0;
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-andersen-hero-slide]'));
      this.navButtons = Array.from(this.querySelectorAll('[data-andersen-hero-nav]'));
      this.prevButton = this.querySelector('[data-andersen-hero-prev]');
      this.nextButton = this.querySelector('[data-andersen-hero-next]');
      this.motionButton = this.querySelector('[data-andersen-hero-motion]');
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = Math.max(parseInt(this.dataset.speed || '7', 10), 5) * 1000;
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';

      if (!this.slides.length) return;

      if (this.reducedMotion) {
        this.classList.add('is-reduced-motion', 'is-paused');
        this.userPaused = true;
      }

      this.navButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.goTo(parseInt(button.dataset.index, 10), true);
        });
      });

      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.previous(true));
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.next(true));
      }

      if (this.motionButton) {
        this.motionButton.addEventListener('click', () => this.toggleMotion());
      }

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.previous(true);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.next(true);
        }
      });

      this.addEventListener('touchstart', (event) => {
        if (!event.changedTouches || !event.changedTouches.length) return;
        this.touchStartX = event.changedTouches[0].clientX;
        this.touchStartY = event.changedTouches[0].clientY;
        this.touchStartTime = Date.now();
      }, { passive: true });

      this.addEventListener('touchend', (event) => {
        if (!event.changedTouches || !event.changedTouches.length) return;
        this.handleSwipe(event.changedTouches[0]);
      }, { passive: true });

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => {
          this.hoverPaused = true;
          this.stopAutoplay();
        });

        this.addEventListener('mouseleave', () => {
          this.hoverPaused = false;
          this.startAutoplay();
        });
      }

      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('resize', this.onResize);

      this.loadSlideMedia(0);
      this.loadSlideMedia(1);
      this.setActive(0, false);
      this.startAutoplay();

      document.addEventListener('shopify:block:select', (event) => {
        const selectedSlide = this.querySelector(`[data-block-id="${event.detail.blockId}"]`);
        if (!selectedSlide) return;
        this.goTo(parseInt(selectedSlide.dataset.index, 10), true);
        this.userPaused = true;
        this.classList.add('is-paused');
        this.stopAutoplay();
      });
    }

    disconnectedCallback() {
      this.stopAutoplay();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('resize', this.onResize);
    }

    onResize() {
      this.updateMotionButtonVisibility();
      if (this.isMobileViewport()) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
        this.pauseInactiveVideos(-1);
      } else {
        this.playActiveVideo();
        this.startAutoplay();
      }
    }

    startAutoplay() {
      if (!this.autoplay || this.isMobileViewport() || this.userPaused || this.hoverPaused || this.reducedMotion || this.slides.length < 2 || document.hidden) {
        return;
      }

      this.stopAutoplay();
      this.classList.remove('is-paused');
      this.timer = window.setInterval(() => this.next(false), this.speed);
    }

    stopAutoplay() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    resetAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    previous(userInitiated = false) {
      const nextIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
      this.goTo(nextIndex, userInitiated);
    }

    next(userInitiated = false) {
      const nextIndex = (this.currentIndex + 1) % this.slides.length;
      this.goTo(nextIndex, userInitiated);
    }

    goTo(index, userInitiated = false) {
      if (Number.isNaN(index) || !this.slides[index]) return;
      if (index === this.currentIndex) {
        this.updateMotionButtonVisibility();
        return;
      }
      this.setActive(index, userInitiated);
    }

    setActive(index, userInitiated = false) {
      this.currentIndex = index;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        slide.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.navButtons.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === index;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      this.loadSlideMedia(index);
      this.loadSlideMedia((index + 1) % this.slides.length);
      this.pauseInactiveVideos(index);
      this.playActiveVideo();
      this.updateMotionButtonVisibility();

      if (userInitiated) {
        this.stopAutoplayAfterInteraction();
      } else {
        this.resetAutoplay();
      }
    }

    loadSlideMedia(index) {
      const slide = this.slides[index];
      if (!slide) return;

      const lazySources = slide.querySelectorAll('source[data-src]');
      lazySources.forEach((source) => {
        source.setAttribute('src', source.dataset.src);
        source.removeAttribute('data-src');
      });

      slide.querySelectorAll('video').forEach((video) => {
        if (video.dataset.loaded === 'true') return;
        try {
          video.load();
          video.dataset.loaded = 'true';
        } catch (error) {
          // Ignore unsupported media/load errors so the image fallback remains visible.
        }
      });
    }

    pauseInactiveVideos(activeIndex) {
      this.slides.forEach((slide, slideIndex) => {
        if (slideIndex === activeIndex) return;
        slide.querySelectorAll('video').forEach((video) => video.pause());
      });
    }

    activeSlideHasPlayableVideo() {
      const activeSlide = this.slides[this.currentIndex];
      if (!activeSlide || this.reducedMotion) return false;

      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const isTablet = window.matchMedia('(min-width: 750px) and (max-width: 989px)').matches;
      const isDesktop = window.matchMedia('(min-width: 990px)').matches;
      const tabletVideoEnabled = this.classList.contains('andersen-hero--tablet-video-enabled');
      const mobileVideoEnabled = this.classList.contains('andersen-hero--mobile-video-enabled');
      const hasDesktopVideo = activeSlide.dataset.hasDesktopVideo === 'true';
      const hasMobileVideo = activeSlide.dataset.hasMobileVideo === 'true';

      if (isDesktop) return hasDesktopVideo;
      if (isTablet) return hasDesktopVideo && tabletVideoEnabled;
      if (isMobile) return hasMobileVideo && mobileVideoEnabled;

      return false;
    }

    getActivePlayableVideos() {
      const activeSlide = this.slides[this.currentIndex];
      if (!activeSlide || !this.activeSlideHasPlayableVideo()) return [];

      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const isTablet = window.matchMedia('(min-width: 750px) and (max-width: 989px)').matches;
      const isDesktop = window.matchMedia('(min-width: 990px)').matches;
      const tabletVideoEnabled = this.classList.contains('andersen-hero--tablet-video-enabled');
      const mobileVideoEnabled = this.classList.contains('andersen-hero--mobile-video-enabled');

      return Array.from(activeSlide.querySelectorAll('[data-andersen-hero-video]')).filter((video) => {
        if (video.classList.contains('andersen-hero__video--desktop')) {
          return isDesktop || (isTablet && tabletVideoEnabled);
        }

        if (video.classList.contains('andersen-hero__video--mobile')) {
          return isMobile && mobileVideoEnabled;
        }

        return false;
      });
    }

    updateMotionButtonVisibility() {
      if (!this.motionButton) return;

      const hasPlayableVideo = this.activeSlideHasPlayableVideo();
      this.motionButton.hidden = !hasPlayableVideo;
      this.motionButton.disabled = !hasPlayableVideo;
      this.motionButton.setAttribute('aria-hidden', hasPlayableVideo ? 'false' : 'true');
      this.motionButton.tabIndex = hasPlayableVideo ? 0 : -1;
      this.classList.toggle('has-active-video', hasPlayableVideo);
    }

    playActiveVideo() {
      if (this.reducedMotion || this.userPaused || document.hidden) return;

      this.getActivePlayableVideos().forEach((video) => {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            // Autoplay can be blocked by the browser. The poster/image fallback remains visible.
          });
        }
      });
    }

    toggleMotion() {
      if (this.motionButton && this.motionButton.hidden) return;
      this.userPaused = !this.userPaused;
      this.classList.toggle('is-paused', this.userPaused);

      if (this.motionButton) {
        this.motionButton.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
      }

      if (this.userPaused) {
        this.stopAutoplay();
        const activeSlide = this.slides[this.currentIndex];
        if (activeSlide) {
          activeSlide.querySelectorAll('video').forEach((video) => video.pause());
        }
      } else {
        this.playActiveVideo();
        this.startAutoplay();
      }
    }

    isMobileViewport() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    stopAutoplayAfterInteraction() {
      if (!this.autoplay) return;
      this.autoplay = false;
      this.stopAutoplay();
    }

    handleSwipe(touch) {
      const deltaX = touch.clientX - this.touchStartX;
      const deltaY = touch.clientY - this.touchStartY;
      const elapsed = Date.now() - this.touchStartTime;

      if (elapsed > 700 || Math.abs(deltaX) < 42 || Math.abs(deltaX) < Math.abs(deltaY) * 1.35) {
        return;
      }

      if (deltaX < 0) {
        this.next(true);
      } else {
        this.previous(true);
      }
    }
  });
}
